// CSS
import './ResponseRating.scss'

// React and Third Party
import { Button, Form, OverlayTrigger, Popover } from 'react-bootstrap'
import { useState } from 'react'
import { saveUserRating } from 'components/common/api/Users'

const ratings = [
  {
    variant: 'bi-hand-thumbs-up',
    activeClass: 'bi-hand-thumbs-up-fill',
    rating: 1
  },
  {
    variant: 'bi-hand-thumbs-down',
    activeClass: 'bi-hand-thumbs-down-fill',
    rating: -1
  }
]

/**
 * Core ResponseRating Component. Used to rate generated responses.
 * @returns {JSX.Element} The ResponseRating Component
 */
export default function ResponseRating({
  responseId,
  style = {},
  className = '',
  setRating = rating => {}
}) {
  const [feedback, setFeedback] = useState('')
  const [ratingSent, setRatingSent] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)
  const [tooltipIndex, setTooltipIndex] = useState(-1)

  const handleRatingClick = async (event, rating) => {
    // Return if rating has already been sent
    if (ratingSent) return

    // Find the button that was clicked
    const button = event.target.closest('button')

    // Get the child icon
    const icon = button.querySelector('i')

    // Add the `activeClass` class to the icon
    icon.classList.add(rating.activeClass)

    // Remove the `variant` class from the icon
    icon.classList.remove(rating.variant)

    // Save the rating, but don't wait for it to finish
    saveUserRating({ id: responseId, rating: rating.rating })

    // Update state
    setRatingSent(true)
    setRating(rating.rating)
  }

  const handleSubmit = () => {
    // Return if feedback has already been sent
    if (feedbackSent) return

    // Save the feedback, but don't wait for it to finish
    saveUserRating({ id: responseId, feedback })

    // Update state
    setFeedbackSent(true)
  }

  // If we don't have a responseId, return null
  if (!responseId) return null

  return (
    <div className={`response-rating ${className}`} style={style}>
      <small className='muted mx-3'>
        <em>Was this helpful?</em>
      </small>
      {ratings.map((rating, index) => (
        <OverlayTrigger
          trigger='click'
          placement='bottom'
          key={index}
          rootClose
          show={tooltipIndex === index}
          onToggle={show => setTooltipIndex(show ? index : -1)}
          overlay={
            <Popover>
              <Popover.Header as='div'>
                {/* Close button */}
                <Button
                  type='button'
                  className='btn-close float-end btn-sm'
                  aria-label='Close'
                  onClick={_ => setTooltipIndex(-1)}
                  title='Close'
                />
                Give us feedback
              </Popover.Header>
              <Popover.Body>
                {/* Did we already submit feedback? */}
                {feedbackSent ? (
                  <div className='text-center'>
                    <p className='mb-0'>Thank you for your feedback!</p>
                    <p className='mb-0'>Your input will help us improve.</p>
                  </div>
                ) : (
                  <>
                    <Form.Control
                      as='textarea'
                      rows={5}
                      className='feedback-input'
                      name='feedback'
                      placeholder='Your feedback'
                      aria-label='Your feedback'
                      value={feedback}
                      onChange={e => setFeedback(e.target.value)}
                      onKeyDown={e => {
                        // Search on enter
                        if (e.key === 'Enter' && feedback.length >= 3)
                          handleSubmit()
                      }}
                    />
                    <div className='text-end mt-2'>
                      <Button
                        variant='secondary'
                        size='sm'
                        className='px-5'
                        onClick={_ => handleSubmit()}
                      >
                        Send
                      </Button>
                    </div>
                  </>
                )}
              </Popover.Body>
            </Popover>
          }
        >
          <Button
            variant='light'
            onClick={event => handleRatingClick(event, rating)}
          >
            {' '}
            <i className={`bi ${rating.variant}`}></i>{' '}
          </Button>
        </OverlayTrigger>
      ))}
    </div>
  )
}
